import { emit, listen } from '@tauri-apps/api/event';
import { getCurrentWindow, Window as TauriWindow } from '@tauri-apps/api/window';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import { RPA_MAIN_WINDOW_LABEL } from '@heidi/plugin-core';

/**
 * Tauri event + route helpers for jumping from the floating overlay
 * to a project's full chat in the main Heidi window.
 *
 * Pattern mirrors `openWorkflowDetail.ts`: the overlay webview emits
 * a Tauri event, the main window (`MainWindowOpenProjectListener`)
 * listens and pushes the corresponding Next.js route. Decoupled from
 * the RPA recorder flag because the overlay agent surface is the only
 * caller and should work whether or not the recorder feature is on.
 */

export const OVERLAY_OPEN_PROJECT_EVENT = 'overlay:open-project';

export function projectDetailHref(projectId: string): string {
  return `/scribe/chat/projects/${encodeURIComponent(projectId)}`;
}

interface OpenProjectPayload {
  readonly projectId: string;
}

async function focusMainWindow(): Promise<void> {
  try {
    if (getCurrentWindow().label !== RPA_MAIN_WINDOW_LABEL) {
      const main = await TauriWindow.getByLabel(RPA_MAIN_WINDOW_LABEL);
      if (main) {
        await main.show();
        await main.unminimize();
        await main.setFocus();
      }
    }
  } catch (error) {
    logger.debug('[overlay/open-project] focus main window failed', {
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

export async function emitOpenProjectInHeidi(projectId: string): Promise<void> {
  if (!projectId) return;

  if (!isTauri()) {
    if (typeof window !== 'undefined') {
      window.location.assign(projectDetailHref(projectId));
    }
    return;
  }

  try {
    await emit(OVERLAY_OPEN_PROJECT_EVENT, { projectId } satisfies OpenProjectPayload);
  } catch (error) {
    logger.warn({
      msg: '[overlay/open-project] failed to emit open-project event',
      error: error instanceof Error ? error.message : String(error),
    });
  }

  await focusMainWindow();
}

export function useMainWindowOpenProjectListener(): void {
  const router = useRouter();

  useEffect(() => {
    if (!isTauri() || getCurrentWindow().label !== RPA_MAIN_WINDOW_LABEL) {
      return;
    }
    let cancelled = false;
    const unlistenPromise = listen<OpenProjectPayload>(
      OVERLAY_OPEN_PROJECT_EVENT,
      (event) => {
        if (cancelled) return;
        const { projectId } = event.payload;
        if (!projectId) return;
        void router.push(projectDetailHref(projectId));
      }
    );
    return () => {
      cancelled = true;
      void unlistenPromise
        .then((fn) => fn())
        .catch((error: unknown) => {
          // If `listen(...)` itself rejected (or the unlisten fn
          // throws on call) we don't have a useful recovery action —
          // the listener was never wired up. Swallow + log so the
          // tear-down doesn't surface as an unhandled rejection.
          logger.debug('[overlay/open-project] listener cleanup failed', {
            error: error instanceof Error ? error.message : String(error),
          });
        });
    };
  }, [router]);
}

export function MainWindowOpenProjectListener(): null {
  useMainWindowOpenProjectListener();
  return null;
}
